import { useMemo, useState } from "react";
import { Search, X, MessageCircle, ChevronDown, ArrowLeft } from "lucide-react";
import { openLogfrogChat } from "../utils/logfrog";
import { useTranslation } from "../context/LanguageContext";
import VideoTutorialsCard from "../components/VideoTutorialsCard";

type SupportFaqSection = {
  titleKey: string;
  descriptionKey: string;
  items: { questionKey: string; answerKey: string }[];
};

const supportFaqSections: SupportFaqSection[] = [
  {
    titleKey: "support.faq.gettingStarted.title",
    descriptionKey: "support.faq.gettingStarted.description",
    items: Array.from({ length: 5 }, (_, i) => ({
      questionKey: `support.faq.gettingStarted.q${i + 1}`,
      answerKey: `support.faq.gettingStarted.a${i + 1}`,
    })),
  },
  {
    titleKey: "support.faq.accountSecurity.title",
    descriptionKey: "support.faq.accountSecurity.description",
    items: Array.from({ length: 5 }, (_, i) => ({
      questionKey: `support.faq.accountSecurity.q${i + 1}`,
      answerKey: `support.faq.accountSecurity.a${i + 1}`,
    })),
  },
  {
    titleKey: "support.faq.productSearchScanning.title",
    descriptionKey: "support.faq.productSearchScanning.description",
    items: Array.from({ length: 5 }, (_, i) => ({
      questionKey: `support.faq.productSearchScanning.q${i + 1}`,
      answerKey: `support.faq.productSearchScanning.a${i + 1}`,
    })),
  },
  {
    titleKey: "support.faq.productsFiltersCategories.title",
    descriptionKey: "support.faq.productsFiltersCategories.description",
    items: Array.from({ length: 5 }, (_, i) => ({
      questionKey: `support.faq.productsFiltersCategories.q${i + 1}`,
      answerKey: `support.faq.productsFiltersCategories.a${i + 1}`,
    })),
  },
  {
    titleKey: "support.faq.productDetailsAlerts.title",
    descriptionKey: "support.faq.productDetailsAlerts.description",
    items: Array.from({ length: 5 }, (_, i) => ({
      questionKey: `support.faq.productDetailsAlerts.q${i + 1}`,
      answerKey: `support.faq.productDetailsAlerts.a${i + 1}`,
    })),
  },
  {
    titleKey: "support.faq.leadsDealDiscovery.title",
    descriptionKey: "support.faq.leadsDealDiscovery.description",
    items: Array.from({ length: 4 }, (_, i) => ({
      questionKey: `support.faq.leadsDealDiscovery.q${i + 1}`,
      answerKey: `support.faq.leadsDealDiscovery.a${i + 1}`,
    })),
  },
  {
    titleKey: "support.faq.specialtyPages.title",
    descriptionKey: "support.faq.specialtyPages.description",
    items: Array.from({ length: 2 }, (_, i) => ({
      questionKey: `support.faq.specialtyPages.q${i + 1}`,
      answerKey: `support.faq.specialtyPages.a${i + 1}`,
    })),
  },
  {
    titleKey: "support.faq.settingsBillingNotifications.title",
    descriptionKey: "support.faq.settingsBillingNotifications.description",
    items: Array.from({ length: 3 }, (_, i) => ({
      questionKey: `support.faq.settingsBillingNotifications.q${i + 1}`,
      answerKey: `support.faq.settingsBillingNotifications.a${i + 1}`,
    })),
  },
  {
    titleKey: "support.faq.helpContactLegal.title",
    descriptionKey: "support.faq.helpContactLegal.description",
    items: Array.from({ length: 4 }, (_, i) => ({
      questionKey: `support.faq.helpContactLegal.q${i + 1}`,
      answerKey: `support.faq.helpContactLegal.a${i + 1}`,
    })),
  },
];

export default function SupportPage() {
  const { t, currentLanguage, setLanguage } = useTranslation();
  const [query, setQuery] = useState("");

  const filteredSections = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const localizedSections = supportFaqSections.map((section) => ({
      title: t(section.titleKey),
      description: t(section.descriptionKey),
      items: section.items.map((item) => ({
        question: t(item.questionKey),
        answer: t(item.answerKey),
      })),
    }));
    if (!normalized) return localizedSections;
    return localizedSections
      .map((section) => {
        const filtered = section.items.filter(
          (item) =>
            item.question.toLowerCase().includes(normalized) ||
            item.answer.toLowerCase().includes(normalized)
        );
        return filtered.length ? { ...section, items: filtered } : null;
      })
      .filter((s): s is (typeof localizedSections)[number] => Boolean(s));
  }, [query, t, currentLanguage]);

  const isSearching = query.trim().length > 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-950">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-8">
        {/* Header row */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <h1 className="text-base font-semibold text-gray-900 dark:text-white">
            {t("support.title")}
          </h1>
          <button
            type="button"
            onClick={() => setLanguage(currentLanguage === "es" ? "en" : "es")}
            className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-md border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 px-2 py-1 text-xs font-medium text-gray-500 dark:text-dark-400 hover:border-gray-300 dark:hover:border-dark-600 transition-colors"
          >
            <span className={currentLanguage === "en" ? "text-gray-900 dark:text-white" : ""}>EN</span>
            <span className="text-gray-300 dark:text-dark-600">/</span>
            <span className={currentLanguage === "es" ? "text-gray-900 dark:text-white" : ""}>ES</span>
          </button>
        </div>

        <VideoTutorialsCard className="mb-3" />

        {/* Search */}
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 px-3 py-2 mb-3">
          <Search size={14} className="text-gray-400 dark:text-dark-500 flex-shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("support.searchPlaceholder")}
            className="w-full bg-transparent text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-dark-500 focus:outline-none"
            aria-label={t("support.searchAriaLabel")}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="text-gray-400 hover:text-gray-600 dark:text-dark-400 dark:hover:text-dark-200 transition-colors"
              aria-label={t("support.clearSearchAriaLabel")}
            >
              <X size={14} />
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={openLogfrogChat}
          className="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-800 px-3 py-2.5 text-sm font-medium text-gray-900 dark:text-white hover:border-gray-300 dark:hover:border-dark-600 transition-colors mb-6"
        >
          <MessageCircle size={16} className="text-gray-400 dark:text-dark-400 flex-shrink-0" />
          {t("support.contactUsButton")}
        </button>

        {/* FAQ sections */}
        {filteredSections.length === 0 && (
          <p className="text-sm text-gray-400 dark:text-dark-500 py-8 text-center">
            No results found.
          </p>
        )}

        <div className="border border-gray-200 dark:border-dark-700 rounded-lg bg-white dark:bg-dark-800 divide-y divide-gray-200 dark:divide-dark-700">
          {filteredSections.map((section) => (
            <details key={section.title} className="group" open={isSearching || undefined}>
              <summary className="cursor-pointer list-none flex items-center justify-between gap-3 px-3.5 py-3 hover:bg-gray-50 dark:hover:bg-dark-750 transition-colors">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{section.title}</p>
                  <p className="text-xs text-gray-400 dark:text-dark-500 mt-0.5 truncate">{section.description}</p>
                </div>
                <ChevronDown size={14} className="text-gray-400 dark:text-dark-500 flex-shrink-0 transition-transform duration-200 group-open:rotate-180" />
              </summary>
              <div className="px-3.5 pb-3 space-y-1">
                {section.items.map((item) => (
                  <details key={item.question} className="group/q rounded-md">
                    <summary className="cursor-pointer list-none flex items-center justify-between gap-3 px-2.5 py-2 text-[13px] text-gray-700 dark:text-dark-300 hover:text-gray-900 dark:hover:text-white rounded-md hover:bg-gray-50 dark:hover:bg-dark-750 transition-colors">
                      <span>{item.question}</span>
                      <ChevronDown size={12} className="text-gray-300 dark:text-dark-600 flex-shrink-0 transition-transform duration-200 group-open/q:rotate-180" />
                    </summary>
                    <p className="px-2.5 pb-2 pt-0.5 text-[13px] text-gray-500 dark:text-dark-400 leading-relaxed">
                      {item.answer}
                    </p>
                  </details>
                ))}
              </div>
            </details>
          ))}
        </div>

        {/* Back */}
        <div className="mt-5">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-gray-400 dark:text-dark-500 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft size={13} />
            {t("support.backToApp")}
          </a>
        </div>
      </div>
    </div>
  );
}
